import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { Redirect, useLocation, useHistory } from 'react-router-dom';

import { useWindowSize } from '../hooks';
import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import InvalidGpxFileInformation from '../components/content/informations/InvalidGpxFileInformation';
import LoadingView from '../components/content/LoadingView';
import GpxMediaList from '../components/content/lists/GpxMediaList';
import withRequestHandler from '../components/hoc/withRequestHandler';
import { UserContext } from '../components/context/UserContext';
import EventCommonPart from '../components/content/event/EventCommonPart';
import EventSearchMainActions from '../components/content/event/EventSearchMainActions';

import { getMainSearchResultUrl, isUserRedirectedFromStrava } from '../utils/common';
import { includesFotoFlat } from '../utils/common';
import {
  MAIN_SEARCH_COMBINED,
  MAIN_SEARCH_FACE,
} from '../utils/variables';
import { customScrollToComponent } from '../utils/common';

////////////////////////////////////////////
// TODO MISSING TESTS: related to useEffect and refetch new media (Relay errors)
////////////////////////////////////////////

const EventGpsSearchPage = ({
  viewer,
  gpxFileIdFromUrl,
  snFromUrl,
  relay,
  onRequestStart,
  onRequestStop,
  eventHasSecondFactor,
  isLoading,
  customerKey,
  codeSecondFactorForFaceEvent,
  stravaActivityId,
  stravaActivityStartDate,
  stravaToken
}) => {
  const { event } = viewer;
  const [showInvalidGpxFileInformation, setShowInvalidGpxFileInformation] =
    useState(false);
  const [searchFormPosition, setSearchFormPosition] = useState(null);
  const { onMarathonFotoChange } = useContext(UserContext);
  const [altSearchPosition, setAltSearchPosition] = useState(null);
  const [shouldShowMainSearch, setShouldShowMainSearch] = useState(false);
  const [elementToAnimate, setElementToAnimate] = useState(null);
  const [isParticipant, setIsParticipant] = useState(false);
  const { windowWidth } = useWindowSize();
  const { search: searchQueryFromUrl, pathname } = useLocation();
  const queryParams = new URLSearchParams(searchQueryFromUrl);
  const history = useHistory();
  const eventOnlineView = useRef();
  const mediaSection = useRef();
  
  const isStravaSearch = pathname.includes('/strava/');
  const isRedirectedFromStrava = isUserRedirectedFromStrava(searchQueryFromUrl);
  
  const hasNoResultsForManual = !isStravaSearch && event.searchMediaByGpx.mediaInfo.count === 0
  const hasNoResultsForStrava = isStravaSearch && event.mediaByStravaActivity.mediaInfo.count === 0
  const hasNoResults = hasNoResultsForManual || hasNoResultsForStrava

  const participantMediaCount = event.participant
    ? event.participant.media.mediaInfo.count
    : 0;

  useEffect(() => {
    onMarathonFotoChange(event.marathonFoto);
    // avoid scrolling after strava auth
    !isRedirectedFromStrava && scrollToMediaSection();
  }, []);

  useEffect(() => {
    refetchMediaForNewGpxFile(gpxFileIdFromUrl);
    // avoid scrolling after strava auth
   !isRedirectedFromStrava && scrollToMediaSection(); 
  }, [gpxFileIdFromUrl]);

  useEffect(() => {
    // workaround because of numbers of calls for gps search
    // TODO rewrite it as other components when backend for GPS search is fixed?
    if (queryParams.get('code2fa')) {
      setIsParticipant(true);
    }
  }, [searchQueryFromUrl]);

  const goBackToMainSearchResults = () => {
    const destinationUrl = getMainSearchResultUrl(
      event.sgId,
      snFromUrl,
      eventHasSecondFactor,
      customerKey,
      searchQueryFromUrl
    );
    history.push(destinationUrl);
  };

  const scrollToForm = () => {
    searchFormPosition &&
      customScrollToComponent({
        currentRef: searchFormPosition.current,
        shouldPassSTickyDesktopBtns: true,
        windowWidth: windowWidth,
      });
  };

  const onScrollToMainSearch = () => {
    setShouldShowMainSearch(true);
    scrollToForm();
    setTimeout(() => {
      setShouldShowMainSearch(false);
    }, 100);
  };

  const liftingUpFormPositionData = (formPositionRef) => {
    setSearchFormPosition(formPositionRef);
  };

  const scrollToMediaSection = () => {
    mediaSection && customScrollToComponent({
      currentRef: mediaSection.current,
      timeout: 400,
      shouldPassSTickyDesktopBtns: true,
      windowWidth: windowWidth,
    });
  };

  const liftingUpAltSearchPositionData = (altSerachPositionRef) => {
    setAltSearchPosition(altSerachPositionRef);
  };

  const scrollToAltSearch = () => {
    altSearchPosition &&
      customScrollToComponent({
        currentRef: altSearchPosition.current,
        shouldPassSTickyDesktopBtns: true,
        windowWidth: windowWidth,
      });
  };

  const refetchMediaForNewGpxFile = (newGpxFileId) => {
    onRequestStart();
    setShowInvalidGpxFileInformation(false);
    relay.refetch({ gpxFileId: newGpxFileId }, null, (err) => {
      if (err) {
        onRequestStop();
        setShowInvalidGpxFileInformation(true);
      } else {
        onRequestStop();
      }
    });
  };

  if (event.state === 'reminder') {
    return <Redirect to={`/event/${event.sgId}`} />;
  }

  return (
    <div className="container-960 container-page">
      <EventCommonPart event={event}>
        <EventSearchMainActions
          onFindMorePhotosClick={scrollToAltSearch}
          event={event}
          onGoBackToMainSearch={goBackToMainSearchResults}
          onScrollToForm={onScrollToMainSearch}
          participantMediaCount={participantMediaCount}
          setElementToAnimate={setElementToAnimate}
          hasEventCombinedSearch={event.mainSearch === MAIN_SEARCH_COMBINED}
        />
        <EventOnlineView
          ref={eventOnlineView}
          onlineEvent={event}
          urlSn={snFromUrl}
          customerKey={customerKey}
          hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
          hasGpsSearchTab={true}
          hasDualSearchTab={event.searchDual}
          setFormRef={liftingUpFormPositionData}
          codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
          showStillProcessingHint={event.pendingMediaProcessing}
          goBackToMainSearchResults={goBackToMainSearchResults}
          setAltSearchRef={liftingUpAltSearchPositionData}
          shouldShowMainSearch={shouldShowMainSearch}
          elementToAnimate={elementToAnimate}
          isParticipant={isParticipant}
        />
      </EventCommonPart>
      <div ref={mediaSection}>
        {isLoading && <LoadingView />}
        {!isLoading && (
          <>
            {!showInvalidGpxFileInformation && !hasNoResults && (
              <GpxMediaList
                eventHasSearchByFace={event.mainSearch === MAIN_SEARCH_FACE}
                eventHasSecondFactor={eventHasSecondFactor}
                eventId={event.sgId}
                startnumber={snFromUrl}
                gpxFileId={gpxFileIdFromUrl}
                gpxFileMedia={
                  isStravaSearch ? event.mediaByStravaActivity : event.searchMediaByGpx
                }
                offer={event}
                participantMediaCount={participantMediaCount}
                goBackToMainSearchResults={goBackToMainSearchResults}
                onScrollToMainSearch={scrollToForm}
                eventHasFotoFlat={includesFotoFlat(event.products)}
                hasStravaToken={isStravaSearch}
                stravaActivityId={stravaActivityId}
                stravaActivityStartDate={stravaActivityStartDate}
                stravaToken={stravaToken}
              />
            )}
            {(showInvalidGpxFileInformation || hasNoResults) && (
              <InvalidGpxFileInformation />
            )}
          </>
        )}
      </div>
      <Faq onFindMore={() => scrollToAltSearch()} />
    </div>
  );
};

EventGpsSearchPage.propTypes = {
  customerKey: PropTypes.string,
  eventHasSecondFactor: PropTypes.bool,
  isLoading: PropTypes.bool,
  snFromUrl: PropTypes.string,
  gpxFileIdFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
  codeSecondFactorForFaceEvent: PropTypes.string,
  stravaActivityId: PropTypes.number,
  stravaActivityStartDate: PropTypes.instanceOf(Date),
  stravaToken: PropTypes.string
};

export { EventGpsSearchPage };

export default createRefetchContainer(
  withRequestHandler(EventGpsSearchPage),
  {
    viewer: graphql`
      fragment EventGpsSearchPage_viewer on Viewer {
        event(id: $eventId, language: $language) {
          id
          sgId
          gps
          hostResultUrl
          products {
            type
          }
          pendingMediaProcessing

          participant(startnumber: $startnumber, secondFactor: $secondFactor)
            @skip(if: $noStartnumber) {
            media {
              mediaInfo {
                count
              }
            }
            startnumber
          }
          searchMediaByGpx(gpxFileId: $gpxFileId) @skip(if: $hasStravaToken) {
            mediaInfo {
              count
            }
            ...GpxMediaList_gpxFileMedia
          }
          mediaByStravaActivity(
            stravaAccessToken: $stravaToken
            stravaActivityId: $stravaActivityId
            stravaActivityStartDate: $stravaActivityStartDate
          ) @include(if: $hasStravaToken) {
            mediaInfo {
              count
            }
            ...GpxMediaList_gpxFileMedia
          }
          secondFactorType {
            type
          }
          mainSearch
          searchDual
          smartSearch
          state
          marathonFoto
          ...EventOnlineView_onlineEvent
          ...GpxMediaList_offer
          ...EventCommonPart_event
          ...EventSearchMainActions_event
        }
      }
    `,
  },
  graphql`
    query EventGpsSearchPageRefetchQuery(
      $eventId: ID!
      $gpxFileId: ID
      $count: Int!
      $cursor: String
      $isRecheckPage: Boolean!
      $stravaToken: String
      $stravaActivityId: Int
      $stravaActivityStartDate: NaiveDatetime
      $hasStravaToken: Boolean!
    ) {
      viewer {
        event(id: $eventId) {
          media: searchMediaByGpx(gpxFileId: $gpxFileId) @skip(if: $hasStravaToken) {
            ...GpxMediaList_gpxFileMedia
          }
          media: mediaByStravaActivity(
            stravaAccessToken: $stravaToken
            stravaActivityId: $stravaActivityId
            stravaActivityStartDate: $stravaActivityStartDate
          ) @include(if: $hasStravaToken) {
            ...GpxMediaList_gpxFileMedia
          }
        }
      }
    }
  `
);
